import { useEffect } from "react";
import Cookies from "js-cookie";
import validateFinalizePaymentData from "./validateFinalizePaymentData";
import { CARD_MODEL } from "../../../../pages/PaymentForm/modules/default";
import { getLoginWithRedirectUrl } from "../../../../../../lib/auth/authRedirectUrl";
import { useLocation } from "react-router";
import config from "../../../../../../config";

export const PROCEED_TO_PAYMENTS_PARAM_NAME = "proceedToPayment";

const FINALIZE_PAYMENT_DATA_COOKIE_NAME = "finalizePaymentData";
const FINALIZE_PAYMENT_DATA_COOKIE_MAX_AGE_MINUTES = 10;
const FINALIZE_PAYMENT_DATA_COOKIE_EXPIRES_DAYS =
  FINALIZE_PAYMENT_DATA_COOKIE_MAX_AGE_MINUTES / (60 * 24);

export const useIsProceedToPayment = () => {
  const location = useLocation();

  return new URLSearchParams(location.search).get(
    PROCEED_TO_PAYMENTS_PARAM_NAME
  );
};

export const getPaymentMethodPricing = ({
  selectedPaymentMethod,
  currentCurrencyOfferByPeriod
}) => {
  const defaultPricing =
    currentCurrencyOfferByPeriod?.payment_methods_pricing?.[0];

  if (typeof defaultPricing === "undefined") {
    return {};
  }

  return (
    currentCurrencyOfferByPeriod?.payment_methods_pricing?.find(
      ({ payment_method }) => payment_method === selectedPaymentMethod?.name
    ) ?? defaultPricing
  );
};

const useOnPaymentButtonClick = ({
  finalizePayment,
  selectedPaymentMethod,
  currentCurrencyOfferByPeriod,
  userData,
  history,
  locale,
  selectedPlan
}) => {
  const { affiliation_discount_amount } =
    currentCurrencyOfferByPeriod?.payment_methods_pricing?.find(
      ({ payment_method }) => payment_method === selectedPaymentMethod?.name
    ) ?? {};

  const hasAffiliationDiscount = Number(affiliation_discount_amount) !== 0;

  useEffect(() => {
    const url = new URL(window.location);

    const finalizePaymentRawData = Cookies.get(
      FINALIZE_PAYMENT_DATA_COOKIE_NAME
    );

    if (!finalizePaymentRawData) {
      return;
    }

    Cookies.remove(FINALIZE_PAYMENT_DATA_COOKIE_NAME, {
      path: "/",
      domain: config.cookieDomain
    });

    if (!userData) {
      return;
    }

    try {
      const finalizePaymentData = JSON.parse(finalizePaymentRawData);

      if (!validateFinalizePaymentData(finalizePaymentData).isValid) {
        url.searchParams.delete(PROCEED_TO_PAYMENTS_PARAM_NAME);

        history.replace({
          pathname: url.pathname,
          hash: url.hash,
          search: url.search
        });

        return;
      }

      finalizePayment(finalizePaymentData);
    } catch (e) {
      console.warn("Failed redirect to payment");
    }
  }, []);

  const onPaymentButtonClick = ({ invoiceFormData }) => {
    const subscriptionMethodId =
      currentCurrencyOfferByPeriod?.subscription_methods?.[0]?.id;

    if (
      selectedPaymentMethod?.requiresSubscriptionId &&
      typeof subscriptionMethodId === "undefined"
    ) {
      console.warn("Missing Subscription Method Id");
      return;
    }

    const finalizePaymentData = {
      paymentMethod: selectedPaymentMethod.name,
      paymentMethodAlias: selectedPaymentMethod.alias,
      cardModel: CARD_MODEL,
      offer: currentCurrencyOfferByPeriod.id,
      subscriptionId: selectedPaymentMethod?.requiresSubscriptionId
        ? currentCurrencyOfferByPeriod.subscription_methods[0].id
        : null,
      agreementContent: selectedPlan?.agreement_content,
      invoiceFormData: invoiceFormData
    };

    if (!userData) {
      try {
        Cookies.set(
          FINALIZE_PAYMENT_DATA_COOKIE_NAME,
          JSON.stringify(finalizePaymentData),
          {
            expires: FINALIZE_PAYMENT_DATA_COOKIE_EXPIRES_DAYS,
            path: "/",
            domain: config.cookieDomain
          }
        );
      } catch (e) {
        console.error("Failed to set finalize payment data cookie", e);
      }

      history.push(getLoginWithRedirectUrl({ locale }));
    } else {
      finalizePayment(finalizePaymentData);
    }
  };

  return {
    onPaymentButtonClick,
    affiliationDiscountAmount: hasAffiliationDiscount
      ? affiliation_discount_amount
      : undefined
  };
};

export default useOnPaymentButtonClick;
